// ---------------------------------------------------------------------------
// Preparation tickets and reserved stock.
//
// There is no "create a ticket" hook, because there is no such endpoint.
// Tickets are written inside the sale and order transactions that cause them,
// so one can never exist for a sale that rolled back. What the screens do is
// read them and print another copy.
// ---------------------------------------------------------------------------

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import type { InventoryReservation, KitchenTicket, ProductAvailability } from '../types';

/// Tickets for one sale or one order.
///
/// Returns an empty array when nothing on it needed cutting - which is most
/// counter sales - so the screen can hide the print button rather than offer
/// one that produces nothing.
export function useKitchenTickets(
  filters: { saleId?: string; orderId?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: ['kitchen-tickets', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.saleId) params.set('saleId', filters.saleId);
      if (filters.orderId) params.set('orderId', filters.orderId);

      const response = await api.get<{ tickets: KitchenTicket[] }>(
        `/kitchen-tickets?${params.toString()}`,
      );
      return response.tickets;
    },
    enabled: enabled && Boolean(filters.saleId ?? filters.orderId),
    staleTime: 30_000,
  });
}

export function useKitchenTicket(id: string | null) {
  return useQuery({
    queryKey: ['kitchen-ticket', id],
    queryFn: async () => {
      const response = await api.get<{ ticket: KitchenTicket }>(`/kitchen-tickets/${id}`);
      return response.ticket;
    },
    enabled: Boolean(id),
  });
}

/// Print another copy.
///
/// Needs `kitchen_tickets.reprint`; without it the server refuses. A second
/// preparation ticket nobody notices is a second cut of meat, so the count is
/// recorded and audited.
export function useReprintKitchenTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ticket: KitchenTicket; message: string }>(`/kitchen-tickets/${id}/reprint`),

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['kitchen-tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['kitchen-ticket'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Reserved stock
// ---------------------------------------------------------------------------

export function useReservations(enabled = true) {
  return useQuery({
    queryKey: ['reservations'],
    queryFn: async () => {
      const response = await api.get<{ reservations: InventoryReservation[] }>('/reservations');
      return response.reservations;
    },
    enabled,
    staleTime: 30_000,
  });
}

/// On the shelf, promised, and genuinely sellable.
///
/// Asked before promising an order, because on-hand stock alone would let the
/// shop sell the same lamb twice.
export function useProductAvailability(productId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['availability', productId],
    queryFn: async () => {
      const response = await api.get<{ availability: ProductAvailability }>(
        `/reservations/availability/${productId}`,
      );
      return response.availability;
    },
    enabled: enabled && Boolean(productId),
    staleTime: 15_000,
  });
}
